/* eslint-disable no-bitwise -- a QR symbol is a bit format; the whole encoder is shifts and xors */

/**
 * A QR encoder, in the repo rather than in a dependency.
 *
 * The app needs one picture of one short URL. Every published package for this
 * carries a renderer, a browser build and a licence file for a job that is a
 * few hundred lines of arithmetic, so it lives here. Byte mode only, which is
 * all a URL needs; the mask is chosen by the penalty rules from the standard,
 * so the output is the same symbol any reader expects.
 *
 * The algorithm follows ISO/IEC 18004. `__tests__/qrCode.test.ts` pins the
 * output against symbols generated by an independent encoder.
 */

/** How much of the symbol can be lost and still read. `medium` is the usual choice for print. */
export type QRErrorCorrectionLevel = 'low' | 'medium' | 'quartile' | 'high';

export interface QRCode {
  /** Modules per side, border excluded. */
  size: number;
  /** `modules[y][x]` — true is a dark module. */
  modules: boolean[][];
  version: number;
  mask: number;
}

const ECC_CODEWORDS_PER_BLOCK: Record<QRErrorCorrectionLevel, number[]> = {
  low: [
    -1, 7, 10, 15, 20, 26, 18, 20, 24, 30, 18, 20, 24, 26, 30, 22, 24, 28, 30, 28, 28, 28, 28, 30, 30, 26, 28, 30, 30,
    30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30,
  ],
  medium: [
    -1, 10, 16, 26, 18, 24, 16, 18, 22, 22, 26, 30, 22, 22, 24, 24, 28, 28, 26, 26, 26, 26, 28, 28, 28, 28, 28, 28, 28,
    28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28,
  ],
  quartile: [
    -1, 13, 22, 18, 26, 18, 24, 18, 22, 20, 24, 28, 26, 24, 20, 30, 24, 28, 28, 26, 30, 28, 30, 30, 30, 30, 28, 30, 30,
    30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30,
  ],
  high: [
    -1, 17, 28, 22, 16, 22, 28, 26, 26, 24, 28, 24, 28, 22, 24, 24, 30, 28, 28, 26, 28, 30, 24, 30, 30, 30, 30, 30, 30,
    30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30,
  ],
};

const NUM_ERROR_CORRECTION_BLOCKS: Record<QRErrorCorrectionLevel, number[]> = {
  low: [
    -1, 1, 1, 1, 1, 1, 2, 2, 2, 2, 4, 4, 4, 4, 4, 6, 6, 6, 6, 7, 8, 8, 9, 9, 10, 12, 12, 12, 13, 14, 15, 16, 17, 18, 19,
    19, 20, 21, 22, 24, 25,
  ],
  medium: [
    -1, 1, 1, 1, 2, 2, 4, 4, 4, 5, 5, 5, 8, 9, 9, 10, 10, 11, 13, 14, 16, 17, 17, 18, 20, 21, 23, 25, 26, 28, 29, 31,
    33, 35, 37, 38, 40, 43, 45, 47, 49,
  ],
  quartile: [
    -1, 1, 1, 2, 2, 4, 4, 6, 6, 8, 8, 8, 10, 12, 16, 12, 17, 16, 18, 21, 20, 23, 23, 25, 27, 29, 34, 34, 35, 38, 40, 43,
    45, 48, 51, 53, 56, 59, 62, 65, 68,
  ],
  high: [
    -1, 1, 1, 2, 4, 4, 4, 5, 6, 8, 8, 11, 11, 16, 16, 18, 16, 19, 21, 25, 25, 25, 34, 30, 32, 35, 37, 40, 42, 45, 48,
    51, 54, 57, 60, 63, 66, 70, 74, 77, 81,
  ],
};

/** The two-bit code each level carries in the format information. */
const FORMAT_BITS: Record<QRErrorCorrectionLevel, number> = { low: 1, medium: 0, quartile: 3, high: 2 };

const MIN_VERSION = 1;
const MAX_VERSION = 40;
const PENALTY_N1 = 3;
const PENALTY_N2 = 3;
const PENALTY_N3 = 40;
const PENALTY_N4 = 10;

function getBit(value: number, index: number): boolean {
  return ((value >>> index) & 1) !== 0;
}

/** Multiplication in GF(2^8) modulo the QR field polynomial x^8 + x^4 + x^3 + x^2 + 1. */
function fieldMultiply(x: number, y: number): number {
  let result = 0;
  for (let i = 7; i >= 0; i -= 1) {
    result = (result << 1) ^ ((result >>> 7) * 0x11d);
    result ^= ((y >>> i) & 1) * x;
  }
  return result;
}

/** Modules in the symbol that are not function patterns, format or version information. */
function getNumRawDataModules(version: number): number {
  let result = (16 * version + 128) * version + 64;
  if (version >= 2) {
    const numAlign = Math.floor(version / 7) + 2;
    result -= (25 * numAlign - 10) * numAlign - 55;
    if (version >= 7) {
      result -= 36;
    }
  }
  return result;
}

function getNumDataCodewords(version: number, level: QRErrorCorrectionLevel): number {
  return (
    Math.floor(getNumRawDataModules(version) / 8) -
    ECC_CODEWORDS_PER_BLOCK[level][version]! * NUM_ERROR_CORRECTION_BLOCKS[level][version]!
  );
}

/** UTF-8 bytes, written out rather than taken from TextEncoder, which not every test environment has. */
function toUtf8(text: string): number[] {
  const bytes: number[] = [];
  for (const codePointText of text) {
    const codePoint = codePointText.codePointAt(0) ?? 0;
    if (codePoint < 0x80) {
      bytes.push(codePoint);
    } else if (codePoint < 0x800) {
      bytes.push(0xc0 | (codePoint >>> 6), 0x80 | (codePoint & 0x3f));
    } else if (codePoint < 0x10000) {
      bytes.push(0xe0 | (codePoint >>> 12), 0x80 | ((codePoint >>> 6) & 0x3f), 0x80 | (codePoint & 0x3f));
    } else {
      bytes.push(
        0xf0 | (codePoint >>> 18),
        0x80 | ((codePoint >>> 12) & 0x3f),
        0x80 | ((codePoint >>> 6) & 0x3f),
        0x80 | (codePoint & 0x3f)
      );
    }
  }
  return bytes;
}

function appendBits(bits: number[], value: number, length: number): void {
  for (let i = length - 1; i >= 0; i -= 1) {
    bits.push((value >>> i) & 1);
  }
}

/** The generator polynomial of the Reed-Solomon code of the given degree. */
function reedSolomonDivisor(degree: number): number[] {
  const result: number[] = Array.from<number>({ length: degree }).fill(0);
  result[degree - 1] = 1;
  let root = 1;
  for (let i = 0; i < degree; i += 1) {
    for (let j = 0; j < degree; j += 1) {
      result[j] = fieldMultiply(result[j]!, root);
      if (j + 1 < degree) {
        result[j] = result[j]! ^ result[j + 1]!;
      }
    }
    root = fieldMultiply(root, 0x02);
  }
  return result;
}

function reedSolomonRemainder(data: number[], divisor: number[]): number[] {
  const result: number[] = Array.from<number>({ length: divisor.length }).fill(0);
  for (const byte of data) {
    const factor = byte ^ result.shift()!;
    result.push(0);
    divisor.forEach((coefficient, i) => {
      result[i] = result[i]! ^ fieldMultiply(coefficient, factor);
    });
  }
  return result;
}

/** Splits the data into blocks, adds the error correction bytes and interleaves the lot. */
function addEccAndInterleave(data: number[], version: number, level: QRErrorCorrectionLevel): number[] {
  const numBlocks = NUM_ERROR_CORRECTION_BLOCKS[level][version]!;
  const blockEccLen = ECC_CODEWORDS_PER_BLOCK[level][version]!;
  const rawCodewords = Math.floor(getNumRawDataModules(version) / 8);
  const numShortBlocks = numBlocks - (rawCodewords % numBlocks);
  const shortBlockLen = Math.floor(rawCodewords / numBlocks);
  const shortBlockDataLen = shortBlockLen - blockEccLen;

  const divisor = reedSolomonDivisor(blockEccLen);
  const blocks: number[][] = [];
  let read = 0;
  for (let i = 0; i < numBlocks; i += 1) {
    const blockData = data.slice(read, read + shortBlockDataLen + (i < numShortBlocks ? 0 : 1));
    read += blockData.length;
    const ecc = reedSolomonRemainder(blockData, divisor);
    if (i < numShortBlocks) {
      // A zero placeholder, so every block is the same length while interleaving; it is skipped below.
      blockData.push(0);
    }
    blocks.push(blockData.concat(ecc));
  }

  const result: number[] = [];
  for (let i = 0; i < blocks[0]!.length; i += 1) {
    blocks.forEach((block, blockIndex) => {
      if (i !== shortBlockDataLen || blockIndex >= numShortBlocks) {
        result.push(block[i]!);
      }
    });
  }
  return result;
}

/** The centres of the alignment patterns, for every version that has them. */
function alignmentPatternPositions(version: number): number[] {
  if (version === 1) {
    return [];
  }
  const numAlign = Math.floor(version / 7) + 2;
  const step = version === 32 ? 26 : Math.ceil((version * 4 + 4) / (numAlign * 2 - 2)) * 2;
  const result: number[] = [6];
  for (let pos = version * 4 + 10; result.length < numAlign; pos -= step) {
    result.splice(1, 0, pos);
  }
  return result;
}

class QRSymbol {
  readonly size: number;

  readonly modules: boolean[][];

  private readonly isFunction: boolean[][];

  constructor(
    private readonly version: number,
    private readonly level: QRErrorCorrectionLevel
  ) {
    this.size = version * 4 + 17;
    this.modules = Array.from({ length: this.size }, () => Array.from<boolean>({ length: this.size }).fill(false));
    this.isFunction = Array.from({ length: this.size }, () => Array.from<boolean>({ length: this.size }).fill(false));
  }

  private setFunctionModule(x: number, y: number, isDark: boolean): void {
    this.modules[y]![x] = isDark;
    this.isFunction[y]![x] = true;
  }

  private drawFinderPattern(x: number, y: number): void {
    for (let dy = -4; dy <= 4; dy += 1) {
      for (let dx = -4; dx <= 4; dx += 1) {
        const distance = Math.max(Math.abs(dx), Math.abs(dy));
        const xx = x + dx;
        const yy = y + dy;
        if (xx >= 0 && xx < this.size && yy >= 0 && yy < this.size) {
          this.setFunctionModule(xx, yy, distance !== 2 && distance !== 4);
        }
      }
    }
  }

  private drawAlignmentPattern(x: number, y: number): void {
    for (let dy = -2; dy <= 2; dy += 1) {
      for (let dx = -2; dx <= 2; dx += 1) {
        this.setFunctionModule(x + dx, y + dy, Math.max(Math.abs(dx), Math.abs(dy)) !== 1);
      }
    }
  }

  drawFormatBits(mask: number): void {
    const data = (FORMAT_BITS[this.level] << 3) | mask;
    let rem = data;
    for (let i = 0; i < 10; i += 1) {
      rem = (rem << 1) ^ ((rem >>> 9) * 0x537);
    }
    const bits = ((data << 10) | rem) ^ 0x5412;

    for (let i = 0; i <= 5; i += 1) {
      this.setFunctionModule(8, i, getBit(bits, i));
    }
    this.setFunctionModule(8, 7, getBit(bits, 6));
    this.setFunctionModule(8, 8, getBit(bits, 7));
    this.setFunctionModule(7, 8, getBit(bits, 8));
    for (let i = 9; i < 15; i += 1) {
      this.setFunctionModule(14 - i, 8, getBit(bits, i));
    }

    for (let i = 0; i < 8; i += 1) {
      this.setFunctionModule(this.size - 1 - i, 8, getBit(bits, i));
    }
    for (let i = 8; i < 15; i += 1) {
      this.setFunctionModule(8, this.size - 15 + i, getBit(bits, i));
    }
    // The dark module, which is always set.
    this.setFunctionModule(8, this.size - 8, true);
  }

  private drawVersionBits(): void {
    if (this.version < 7) {
      return;
    }
    let rem = this.version;
    for (let i = 0; i < 12; i += 1) {
      rem = (rem << 1) ^ ((rem >>> 11) * 0x1f25);
    }
    const bits = (this.version << 12) | rem;
    for (let i = 0; i < 18; i += 1) {
      const bit = getBit(bits, i);
      const a = this.size - 11 + (i % 3);
      const b = Math.floor(i / 3);
      this.setFunctionModule(a, b, bit);
      this.setFunctionModule(b, a, bit);
    }
  }

  drawFunctionPatterns(): void {
    for (let i = 0; i < this.size; i += 1) {
      this.setFunctionModule(6, i, i % 2 === 0);
      this.setFunctionModule(i, 6, i % 2 === 0);
    }

    this.drawFinderPattern(3, 3);
    this.drawFinderPattern(this.size - 4, 3);
    this.drawFinderPattern(3, this.size - 4);

    const positions = alignmentPatternPositions(this.version);
    const last = positions.length - 1;
    positions.forEach((posY, i) => {
      positions.forEach((posX, j) => {
        const isFinderCorner = (i === 0 && j === 0) || (i === 0 && j === last) || (i === last && j === 0);
        if (!isFinderCorner) {
          this.drawAlignmentPattern(posX, posY);
        }
      });
    });

    // A placeholder mask; the real one is written once it is chosen.
    this.drawFormatBits(0);
    this.drawVersionBits();
  }

  drawCodewords(data: number[]): void {
    let i = 0;
    // Right to left in pairs of columns, skipping the vertical timing pattern:
    // the pair that would sit on column 6 is shifted one to the left.
    let right = this.size - 1;
    while (right >= 1) {
      if (right === 6) {
        right = 5;
      }
      for (let vert = 0; vert < this.size; vert += 1) {
        for (let j = 0; j < 2; j += 1) {
          const x = right - j;
          const isUpward = ((right + 1) & 2) === 0;
          const y = isUpward ? this.size - 1 - vert : vert;
          if (this.isFunction[y]![x] !== true && i < data.length * 8) {
            this.modules[y]![x] = getBit(data[i >>> 3]!, 7 - (i & 7));
            i += 1;
          }
        }
      }
      right -= 2;
    }
  }

  applyMask(mask: number): void {
    for (let y = 0; y < this.size; y += 1) {
      for (let x = 0; x < this.size; x += 1) {
        let invert: boolean;
        switch (mask) {
          case 0:
            invert = (x + y) % 2 === 0;
            break;
          case 1:
            invert = y % 2 === 0;
            break;
          case 2:
            invert = x % 3 === 0;
            break;
          case 3:
            invert = (x + y) % 3 === 0;
            break;
          case 4:
            invert = (Math.floor(x / 3) + Math.floor(y / 2)) % 2 === 0;
            break;
          case 5:
            invert = ((x * y) % 2) + ((x * y) % 3) === 0;
            break;
          case 6:
            invert = (((x * y) % 2) + ((x * y) % 3)) % 2 === 0;
            break;
          default:
            invert = (((x + y) % 2) + ((x * y) % 3)) % 2 === 0;
            break;
        }
        if (invert && this.isFunction[y]![x] !== true) {
          this.modules[y]![x] = this.modules[y]![x] !== true;
        }
      }
    }
  }

  private finderPenaltyAddHistory(runLength: number, history: number[]): void {
    const length = history[0] === 0 ? runLength + this.size : runLength;
    history.pop();
    history.unshift(length);
  }

  // eslint-disable-next-line class-methods-use-this -- one of a set of penalty helpers; the others need the symbol
  private finderPenaltyCountPatterns(history: number[]): number {
    const n = history[1]!;
    const core = n > 0 && history[2] === n && history[3] === n * 3 && history[4] === n && history[5] === n;
    return (
      (core && history[0]! >= n * 4 && history[6]! >= n ? 1 : 0) +
      (core && history[6]! >= n * 4 && history[0]! >= n ? 1 : 0)
    );
  }

  private finderPenaltyTerminate(runColor: boolean, runLengthInput: number, history: number[]): number {
    let runLength = runLengthInput;
    if (runColor) {
      this.finderPenaltyAddHistory(runLength, history);
      runLength = 0;
    }
    // The quiet zone counts as light modules on the far side.
    runLength += this.size;
    this.finderPenaltyAddHistory(runLength, history);
    return this.finderPenaltyCountPatterns(history);
  }

  penaltyScore(): number {
    let result = 0;

    for (let y = 0; y < this.size; y += 1) {
      let runColor = false;
      let runLength = 0;
      const history = [0, 0, 0, 0, 0, 0, 0];
      for (let x = 0; x < this.size; x += 1) {
        if (this.modules[y]![x] === runColor) {
          runLength += 1;
          if (runLength === 5) {
            result += PENALTY_N1;
          } else if (runLength > 5) {
            result += 1;
          }
        } else {
          this.finderPenaltyAddHistory(runLength, history);
          if (!runColor) {
            result += this.finderPenaltyCountPatterns(history) * PENALTY_N3;
          }
          runColor = this.modules[y]![x]!;
          runLength = 1;
        }
      }
      result += this.finderPenaltyTerminate(runColor, runLength, history) * PENALTY_N3;
    }

    for (let x = 0; x < this.size; x += 1) {
      let runColor = false;
      let runLength = 0;
      const history = [0, 0, 0, 0, 0, 0, 0];
      for (let y = 0; y < this.size; y += 1) {
        if (this.modules[y]![x] === runColor) {
          runLength += 1;
          if (runLength === 5) {
            result += PENALTY_N1;
          } else if (runLength > 5) {
            result += 1;
          }
        } else {
          this.finderPenaltyAddHistory(runLength, history);
          if (!runColor) {
            result += this.finderPenaltyCountPatterns(history) * PENALTY_N3;
          }
          runColor = this.modules[y]![x]!;
          runLength = 1;
        }
      }
      result += this.finderPenaltyTerminate(runColor, runLength, history) * PENALTY_N3;
    }

    for (let y = 0; y < this.size - 1; y += 1) {
      for (let x = 0; x < this.size - 1; x += 1) {
        const color = this.modules[y]![x];
        if (
          color === this.modules[y]![x + 1] &&
          color === this.modules[y + 1]![x] &&
          color === this.modules[y + 1]![x + 1]
        ) {
          result += PENALTY_N2;
        }
      }
    }

    let dark = 0;
    for (const row of this.modules) {
      for (const module of row) {
        if (module) {
          dark += 1;
        }
      }
    }
    const total = this.size * this.size;
    const k = Math.ceil(Math.abs(dark * 20 - total * 10) / total) - 1;
    return result + k * PENALTY_N4;
  }
}

/**
 * Encodes `text` as a QR symbol in byte mode.
 *
 * The version is the smallest one the text fits in at the requested level, and
 * the mask is the one the standard's penalty rules prefer.
 */
export function generateQRCode(text: string, level: QRErrorCorrectionLevel = 'medium'): QRCode {
  const bytes = toUtf8(text);

  let version = MIN_VERSION;
  let dataCapacityBits = 0;
  for (; version <= MAX_VERSION; version += 1) {
    dataCapacityBits = getNumDataCodewords(version, level) * 8;
    const charCountBits = version <= 9 ? 8 : 16;
    if (4 + charCountBits + bytes.length * 8 <= dataCapacityBits) {
      break;
    }
  }
  if (version > MAX_VERSION) {
    throw new Error('Text is too long for a QR code');
  }

  const bits: number[] = [];
  appendBits(bits, 0b0100, 4);
  appendBits(bits, bytes.length, version <= 9 ? 8 : 16);
  for (const byte of bytes) {
    appendBits(bits, byte, 8);
  }
  appendBits(bits, 0, Math.min(4, dataCapacityBits - bits.length));
  appendBits(bits, 0, (8 - (bits.length % 8)) % 8);
  for (let padByte = 0xec; bits.length < dataCapacityBits; padByte ^= 0xec ^ 0x11) {
    appendBits(bits, padByte, 8);
  }

  const dataCodewords: number[] = Array.from<number>({ length: bits.length / 8 }).fill(0);
  bits.forEach((bit, i) => {
    dataCodewords[i >>> 3] = dataCodewords[i >>> 3]! | (bit << (7 - (i & 7)));
  });

  const symbol = new QRSymbol(version, level);
  symbol.drawFunctionPatterns();
  symbol.drawCodewords(addEccAndInterleave(dataCodewords, version, level));

  let mask = 0;
  let minPenalty = Infinity;
  for (let candidate = 0; candidate < 8; candidate += 1) {
    symbol.applyMask(candidate);
    symbol.drawFormatBits(candidate);
    const penalty = symbol.penaltyScore();
    if (penalty < minPenalty) {
      minPenalty = penalty;
      mask = candidate;
    }
    symbol.applyMask(candidate);
  }
  symbol.applyMask(mask);
  symbol.drawFormatBits(mask);

  return { size: symbol.size, modules: symbol.modules, version, mask };
}
